import { useState } from 'react'
import type { LabelTemplateResponse, PrinterDriver } from '@alpha-edge/shared'
import { Modal, FormField, btnPrimary, btnSecondary, colors, input, LabelPreview } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { inferZebraDimensions, formatZebraPaperSize } from '../../utils/label-format'

interface Props {
  readonly isOpen: boolean
  readonly onClose: () => void
  /** Called after a successful import; `unknownTags` is non-empty when the body has
   *  unrecognized `{{tag}}` placeholders (warning, not a failure — mirrors create/update). */
  readonly onImported: (unknownTags?: readonly string[]) => void
}

type Step = 'select' | 'edit'

const DEFAULT_DRIVER: PrinterDriver = 'dymo_client'

function nameFromFilename(filename: string): string {
  return filename.replace(/\.[^./]+$/, '')
}

const errorBanner: React.CSSProperties = {
  padding: '0.625rem 0.875rem',
  marginBottom: '1rem',
  color: colors.danger,
  background: colors.bgError,
  border: `1px solid ${colors.borderError}`,
  borderRadius: '4px',
  fontSize: '0.875rem',
}

export function LabelTemplateImportModal({ isOpen, onClose, onImported }: Props) {
  const [step, setStep] = useState<Step>('select')
  const [driver, setDriver] = useState<PrinterDriver>(DEFAULT_DRIVER)
  const [file, setFile] = useState<File | null>(null)
  const [fileBody, setFileBody] = useState('')
  const [name, setName] = useState('')
  const [paperSize, setPaperSize] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function resetState() {
    setStep('select')
    setDriver(DEFAULT_DRIVER)
    setFile(null)
    setFileBody('')
    setName('')
    setPaperSize('')
    setDescription('')
    setError(null)
    setSubmitting(false)
  }

  function handleClose() {
    resetState()
    onClose()
  }

  function returnToSelect() {
    setStep('select')
    setFile(null)
    setFileBody('')
    setError(null)
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0]
    // Reset so the same filename can be re-selected after "choose a different file"
    e.target.value = ''
    if (!selected) return
    setError(null)

    const reader = new FileReader()
    reader.onload = () => {
      const body = typeof reader.result === 'string' ? reader.result : ''
      setFile(selected)
      setFileBody(body)
      setName(nameFromFilename(selected.name))
      if (driver === 'zebra_browser_print') {
        const dims = inferZebraDimensions(body)
        setPaperSize(dims ? formatZebraPaperSize(dims) : '')
      } else {
        setPaperSize('')
      }
      setStep('edit')
    }
    reader.onerror = () => setError('Failed to read the selected file.')
    reader.readAsText(selected)
  }

  async function handleUpload() {
    if (!file) return
    setSubmitting(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('name', name)
      formData.append('driver', driver)
      if (paperSize) formData.append('paperSize', paperSize)
      if (description) formData.append('description', description)

      const res = await api.postForm<LabelTemplateResponse>('/admin/label-templates/import', formData)
      const unknownTags = (res as { warnings?: { unknownTags?: readonly string[] } }).warnings?.unknownTags
      onImported(unknownTags)
      handleClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Import failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Label Format" width="520px">
      {error && <div style={errorBanner}>{error}</div>}

      {step === 'select' && (
        <div>
          <p style={{ color: colors.text, marginTop: 0 }}>
            Upload a legacy or exported label format file (<code>.zpl</code>, <code>.dymo</code>, <code>.xml</code>,{' '}
            <code>.label</code>, <code>.txt</code>). It will be validated and stored as a custom template for your
            organization.
          </p>

          <div style={{ marginBottom: '1rem' }}>
            <FormField name="import-driver" label="Driver" required>
              <select
                id="import-driver"
                style={input}
                value={driver}
                onChange={(e) => setDriver(e.target.value as PrinterDriver)}
                data-testid="import-driver-select"
              >
                <option value="dymo_client">DYMO</option>
                <option value="zebra_browser_print">Zebra Browser Print</option>
              </select>
            </FormField>
          </div>

          <label
            style={{
              display: 'block',
              border: `2px dashed ${colors.border}`,
              borderRadius: '6px',
              padding: '2rem',
              textAlign: 'center',
              cursor: 'pointer',
              color: colors.textMuted,
              position: 'relative',
            }}
          >
            <span>Click to select a label format file</span>
            <input
              type="file"
              accept=".zpl,.dymo,.xml,.label,.txt"
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
              onChange={handleFileChange}
              data-testid="import-file-input"
            />
          </label>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button type="button" style={btnSecondary} onClick={handleClose}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 'edit' && file && (
        <div>
          <div
            style={{
              background: colors.bgMuted,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              color: colors.text,
            }}
          >
            {file.name} &middot; {driver === 'dymo_client' ? 'DYMO' : 'Zebra Browser Print'}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <FormField name="import-name" label="Name" required value={name} onChange={setName} />
            <FormField
              name="import-paper-size"
              label="Paper Size"
              value={paperSize}
              onChange={setPaperSize}
              placeholder="e.g. zebra_2x1"
            />
            <FormField name="import-description" label="Description" value={description} onChange={setDescription} />
          </div>

          <div style={{ marginTop: '1rem' }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: colors.text, marginBottom: '0.375rem' }}>
              Preview
            </div>
            <LabelPreview driver={driver} body={fileBody} paperSize={paperSize} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button type="button" style={btnSecondary} onClick={returnToSelect} disabled={submitting}>
              Choose a different file
            </button>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" style={btnSecondary} onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                style={btnPrimary}
                onClick={() => void handleUpload()}
                disabled={submitting || !name}
                data-testid="import-upload-btn"
              >
                {submitting ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
